import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import {
  BuildingIcon,
  CalendarIcon,
  ChevronDownIcon,
  CircleCheckIcon,
  InfoIcon,
  LoaderIcon,
  PackageIcon,
  PlusIcon,
  TruckIcon,
  UserIcon,
} from '@hero/web-icons';
import {
  agentLocationToOrderDraft,
  coerceAgentOrderDraft,
  draftLocationToAgentPoint,
  fromAgentOrderDraft,
  formatPickupWindow,
  OrderDraftProvider,
  orderDraftLocationToAgent,
  readAgentPickupDate,
  toAgentStagingPayload,
  useOrderDraftStore,
  vehiclesFromOrderDraftLoad,
  type AgentDestinationLane,
  type AgentLocationPoint,
  type AgentOrderDriver,
  type AgentOrderItem,
} from '@hero/hooks-shared';
import type { PublishOrderLoadDraft, PublishOrderLocationDraft } from '@hero/hooks-shared';
import { getDeviceTimezone } from '@hero/core';
import { stagedPlaceLabels } from '@hero/agent';
import {
  useOrderDraftApplyClientDefaultsManager,
  useOrderDraftPricingManager,
} from '@hero/web-hooks';
import { Counter } from '../../../app/Counter/Counter';
import { Button } from '../../../ui/Button/Button';
import { PickupDateDrawer } from '../../Order/PickupDate/PickupDateDrawer';
import { OrderPriceEditDrawer } from '../../Order/Drawer/OrderPriceEditDrawer';
import { LocationPickerDrawer } from '../../Order/Publish/PublishOrderPage/LocationPickerDrawer';
import { LocationPickerRow } from '../../Order/Publish/PublishOrderPage/LocationPickerRow';
import { DestinationPriceChip } from '../../Order/Publish/PublishOrderPage/DestinationPriceChip';
import { useAgentChatStore } from './store';
import { useReportCardBusy } from './useCardBusy';

type PickerSlot = { kind: 'origin' } | { kind: 'destination'; index: number };

/** "ظفار · KSZ1234" / "KSZ1234" — compact staged-driver label. */
const stagedDriverLabel = (driver?: AgentOrderDriver): string | undefined => {
  if (!driver) return undefined;
  const name = driver.name?.trim() || driver.phone?.trim();
  const plate = driver.plate?.trim();
  if (name && plate) return `${name} · ${plate}`;
  return name || plate || undefined;
};

/**
 * Agent confirmation-card variant of the publish-order route section.
 *
 * Mounts a scoped `OrderDraftProvider` per confirmation card so each
 * card carries its own draft state, pricing preview, and per-leg
 * overrides. Hydrates from the agent's staging payload on first
 * render and pushes every subsequent edit back to the chat store via
 * `onChange` → `toAgentStagingPayload` → `updatePendingConfirmationPayload`.
 * That keeps the staged payload the server sees on Confirm in lock-step
 * with what the user just edited, regardless of how many other
 * confirmation cards are open (one client, two clients, more — each
 * card gets its own provider so multi-client conversations Just Work).
 *
 * The inner `RouteSection` reads from `useOrderDraftStore`, so it
 * shares the same `priceOverrides` / `pricePreview` model as the
 * publish page — no more drift between the chat and the new-order
 * page.
 */
export const CreateOrderPayloadPreview = ({
  confirmationId,
  payload,
  expanded,
  onToggleExpanded,
  interactionsDisabled = false,
  isProcessing = false,
  isDone = false,
}: {
  /**
   * The card's `confirmationId` (one per `prepare_create_order_draft`
   * tool call this turn). Used to scope edits — when the user changes a
   * destination in the Berain card, we only write back to the Berain
   * card's payload, never the Arasco one rendered next to it.
   */
  confirmationId: string;
  payload: Record<string, unknown>;
  /**
   * Whether the full editor is visible. When `false` the card renders
   * a one-line summary row only — the scoped `OrderDraftProvider`
   * stays mounted so user edits are preserved across collapse cycles,
   * but the heavy resolver pricing and location pickers don't mount
   * (a big win for bulk multi-client turns where 30 cards would
   * otherwise fire 30 simultaneous pricing requests on first render).
   */
  expanded: boolean;
  /** Toggle the editor open/closed (chevron click handler). */
  onToggleExpanded: () => void;
  /** Locks summary-row expand during Confirm-all. */
  interactionsDisabled?: boolean;
  /** This card's confirm POST is in flight. */
  isProcessing?: boolean;
  /** This card finished successfully in the current batch. */
  isDone?: boolean;
}) => {
  const updatePayload = useAgentChatStore(s => s.updatePendingConfirmationPayload);

  const initialDraft = useMemo(() => coerceAgentOrderDraft(payload), [payload]);
  const initialPickup = useMemo(() => readAgentPickupDate(payload), [payload]);
  const initialState = useMemo(() => {
    const hydrated = fromAgentOrderDraft(initialDraft);
    return {
      customer: hydrated.customer,
      origin: hydrated.origin,
      destinations:
        hydrated.destinations.length > 0
          ? hydrated.destinations
          : [{ name: '', lat: 0, lng: 0 }],
      destinationVehicleQuantities:
        hydrated.destinationVehicleQuantities.length > 0
          ? hydrated.destinationVehicleQuantities
          : [1],
      pickupDate: hydrated.pickupDate ?? initialPickup,
      priceOverrides:
        hydrated.priceOverrides.length > 0 ? hydrated.priceOverrides : [undefined],
      load: hydrated.load,
    };
  }, [initialDraft, initialPickup]);

  const handleChange = useCallback(
    (state: {
      customer?: { customerId?: string; name: string } | null;
      origin: PublishOrderLocationDraft | null;
      destinations: PublishOrderLocationDraft[];
      destinationVehicleQuantities: number[];
      pickupDate: { from: string; to: string } | null;
      priceOverrides: Array<
        { itemsOriginalSubtotal: number; currency: string; taxable: boolean } | undefined
      >;
      // Only the two fields `vehiclesFromOrderDraftLoad` reads. Uses
      // `Pick<PublishOrderLoadDraft, ...>` (rather than the previous
      // ad-hoc `{ vehicleType?: string; vehicleTypes?: string[] }`) so
      // the field types stay in lock-step with the strict
      // `VehicleTypes` enum on `PublishOrderLoadDraft`. Without this,
      // `next build` on landing-v2 (the only surface that runs strict
      // `tsc --noEmit` in CI) rejected the call at line ~165 below.
      load: Pick<PublishOrderLoadDraft, 'vehicleType' | 'vehicleTypes'>;
    }) => {
      const toAgentPoint = (
        loc: PublishOrderLocationDraft | null,
      ): AgentLocationPoint | undefined => {
        if (!loc) return undefined;
        if (loc.lat !== 0 && loc.lng !== 0) {
          return orderDraftLocationToAgent(loc);
        }
        return draftLocationToAgentPoint(loc);
      };

      const origin: AgentLocationPoint = toAgentPoint(state.origin) ?? {};
      const destinations: AgentDestinationLane[] = state.destinations.map((d, i) => ({
        location: toAgentPoint(d),
        vehicleQuantity: state.destinationVehicleQuantities[i] ?? 1,
        price: state.priceOverrides[i]
          ? {
              itemsOriginalSubtotal: state.priceOverrides[i]!.itemsOriginalSubtotal,
              currency: state.priceOverrides[i]!.currency,
              taxable: state.priceOverrides[i]!.taxable,
            }
          : undefined,
      }));
      updatePayload(
        confirmationId,
        toAgentStagingPayload({
          clientId: state.customer?.customerId,
          clientName: state.customer?.name,
          pickupDate: state.pickupDate,
          vehicles: vehiclesFromOrderDraftLoad(state.load),
          origin,
          destinations,
          // Staged fields the editor doesn't touch — must round-trip
          // through every edit or the confirm payload loses them.
          items: initialDraft.items,
          notes: initialDraft.notes,
          driver: initialDraft.driver,
          noClientHint: initialDraft.noClientHint,
        }),
      );
    },
    [updatePayload, confirmationId, initialDraft],
  );

  return (
    <OrderDraftProvider mode='create' initialState={initialState} onChange={handleChange}>
      <OrderDraftManagers confirmationId={confirmationId} />
      <OrderDraftSummaryRow
        fallbackClientName={initialDraft.clientName}
        driverLabel={stagedDriverLabel(initialDraft.driver)}
        expanded={expanded}
        onToggleExpanded={onToggleExpanded}
        interactionsDisabled={interactionsDisabled}
        isProcessing={isProcessing}
        isDone={isDone}
      />
      {expanded ? (
        <CreateOrderPayloadPreviewInner
          confirmationId={confirmationId}
          clientName={initialDraft.clientName}
          items={initialDraft.items}
          notes={initialDraft.notes}
          driver={initialDraft.driver}
          noClientHint={initialDraft.noClientHint}
        />
      ) : null}
    </OrderDraftProvider>
  );
};

/**
 * Compact one-line summary of the current draft state — visible
 * whether or not the editor is expanded. Reads from the same scoped
 * `OrderDraftProvider` as the editor so a destination edit instantly
 * updates the row.
 *
 * Designed to stay on a single line at typical chat-panel widths
 * (wraps cleanly on tiny viewports). Bulk-confirmation turns lean on
 * this: 30 cards stacked = ~30 short rows, scannable at a glance.
 */
const OrderDraftSummaryRow = ({
  fallbackClientName,
  driverLabel,
  expanded,
  onToggleExpanded,
  interactionsDisabled = false,
  isProcessing = false,
  isDone = false,
}: {
  fallbackClientName?: string;
  /** Pre-assigned driver ("ظفار · KSZ1234") — staged, read-only. */
  driverLabel?: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  interactionsDisabled?: boolean;
  isProcessing?: boolean;
  isDone?: boolean;
}) => {
  const { t } = useTranslation(['m-agent', 'c-dates']);
  const customer = useOrderDraftStore(s => s.customer);
  const origin = useOrderDraftStore(s => s.origin);
  const destinations = useOrderDraftStore(s => s.destinations);
  const destinationVehicleQuantities = useOrderDraftStore(
    s => s.destinationVehicleQuantities,
  );
  const pickupDate = useOrderDraftStore(s => s.pickupDate);

  const clientLabel =
    customer?.name?.trim() ||
    fallbackClientName?.trim() ||
    t('confirmation.order.internal', { defaultValue: 'Internal order' });

  const resolvedDestinations = destinations.filter(
    d => (d?.lat !== 0 && d?.lng !== 0) || Boolean(d?.name?.trim()),
  );

  // City, never the warehouse name — the header is the route, and a
  // route reads the same everywhere (card, WhatsApp, order list).
  const originLabel =
    stagedPlaceLabels(origin ?? undefined).city ||
    t('confirmation.order.origin_missing', { defaultValue: 'No origin' });

  const firstDestLabel =
    stagedPlaceLabels(resolvedDestinations[0]).city ||
    t('confirmation.order.destination_missing', { defaultValue: 'No destination' });

  const extraDestCount = Math.max(0, resolvedDestinations.length - 1);

  const totalTrucks = destinationVehicleQuantities.reduce(
    (sum, q) => sum + (q ?? 0),
    0,
  );

  const pickupLabel = pickupDate
    ? formatPickupWindow(
        { from: pickupDate.from, to: pickupDate.to },
        getDeviceTimezone() ?? 'Asia/Riyadh',
      )
    : '';

  return (
    <button
      type='button'
      onClick={() => {
        if (interactionsDisabled) return;
        onToggleExpanded();
      }}
      disabled={interactionsDisabled}
      aria-expanded={expanded}
      className={cn(
        'flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl px-4 py-2.5 text-start text-xs transition-colors',
        interactionsDisabled ? 'cursor-default opacity-90' : 'hover:bg-slate-50',
        isDone && 'bg-emerald-50/40',
      )}
    >
      {isProcessing ? (
        <LoaderIcon className='size-4 shrink-0 animate-spin text-primary-600' />
      ) : isDone ? (
        <CircleCheckIcon className='size-4 shrink-0 text-emerald-600' />
      ) : null}

      <span className='flex min-w-0 items-center gap-1.5 text-sm font-medium text-slate-800'>
        <BuildingIcon className='size-3.5 shrink-0 text-slate-400' />
        <span className='truncate'>{clientLabel}</span>
      </span>

      <span className='flex min-w-0 items-center gap-1 text-slate-600'>
        <span className='truncate'>{originLabel}</span>
        <span className='text-slate-400'>→</span>
        <span className='truncate'>{firstDestLabel}</span>
        {extraDestCount > 0 ? (
          <span className='shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600'>
            +{extraDestCount}
          </span>
        ) : null}
      </span>

      <span className='flex shrink-0 items-center gap-1 text-slate-500'>
        <TruckIcon className='size-3.5' />
        <span>{totalTrucks}</span>
      </span>

      {driverLabel ? (
        <span className='flex min-w-0 shrink-0 items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600'>
          <UserIcon className='size-3' />
          <span className='max-w-32 truncate'>{driverLabel}</span>
        </span>
      ) : null}

      {pickupLabel ? (
        <span className='flex shrink-0 items-center gap-1 text-slate-500'>
          <CalendarIcon className='size-3.5' />
          <span className='truncate'>{pickupLabel}</span>
        </span>
      ) : null}

      {!interactionsDisabled ? (
        <span className='ms-auto flex shrink-0 items-center gap-1 text-[11px] font-medium text-slate-500'>
          <span>
            {expanded
              ? t('confirmation.order.collapse', { defaultValue: 'Hide' })
              : t('confirmation.order.expand', { defaultValue: 'Edit' })}
          </span>
          <ChevronDownIcon
            className={cn('size-3.5 transition-transform', expanded && 'rotate-180')}
          />
        </span>
      ) : null}
    </button>
  );
};

/**
 * Resolver + defaults managers for the scoped agent draft. Mounted
 * at the card level (not only when expanded) so matched Route vehicles
 * and client/org preferences seed before the user opens the editor —
 * same behavior as `PublishOrderPage`.
 *
 * Also the only place that knows a quote is in flight, so it publishes
 * that to the chat store: a location edit re-prices, and confirming
 * before the new price lands would create the order at the old one.
 */
const OrderDraftManagers = ({ confirmationId }: { confirmationId: string }) => {
  useOrderDraftApplyClientDefaultsManager();
  const pricing = useOrderDraftPricingManager();
  useReportCardBusy(confirmationId, 'price', pricing.isPending);
  return null;
};

const CreateOrderPayloadPreviewInner = ({
  confirmationId,
  clientName,
  items,
  notes,
  driver,
  noClientHint,
}: {
  confirmationId: string;
  clientName?: string;
  items?: AgentOrderItem[];
  notes?: string;
  driver?: AgentOrderDriver;
  noClientHint?: boolean;
}) => {
  const { t } = useTranslation(['m-agent', 'm-order', 'c-dates']);

  const origin = useOrderDraftStore(s => s.origin);
  const destinations = useOrderDraftStore(s => s.destinations);
  const destinationVehicleQuantities = useOrderDraftStore(s => s.destinationVehicleQuantities);
  const pickupDate = useOrderDraftStore(s => s.pickupDate);
  const customer = useOrderDraftStore(s => s.customer);

  const setOrigin = useOrderDraftStore(s => s.setOrigin);
  const setDestination = useOrderDraftStore(s => s.setDestination);
  const addDestination = useOrderDraftStore(s => s.addDestination);
  const removeDestination = useOrderDraftStore(s => s.removeDestination);
  const setDestinationVehicleQuantity = useOrderDraftStore(s => s.setDestinationVehicleQuantity);
  const setPickupDate = useOrderDraftStore(s => s.setPickupDate);

  const [pickerSlot, setPickerSlot] = useState<PickerSlot | null>(null);
  const [priceEditIndex, setPriceEditIndex] = useState<number | null>(null);
  const [isDateOpen, setDateOpen] = useState(false);

  // A half-finished location pick is not a confirmable order — and the
  // stack's Confirm all sits outside this drawer, well within reach.
  useReportCardBusy(confirmationId, 'location', pickerSlot !== null);

  const handlePick = (loc: PublishOrderLocationDraft) => {
    if (!pickerSlot) return;
    if (pickerSlot.kind === 'origin') setOrigin(loc);
    else setDestination(pickerSlot.index, loc);
    setPickerSlot(null);
  };

  const excludeLocation: PublishOrderLocationDraft | null =
    pickerSlot?.kind === 'origin'
      ? origin
      : pickerSlot?.kind === 'destination'
        ? destinations[pickerSlot.index] ?? null
        : null;

  const slotInitialEdit = useMemo(() => {
    if (!excludeLocation || excludeLocation.lat === 0) return undefined;
    if (excludeLocation.savedLocationId) {
      return { savedLocationId: excludeLocation.savedLocationId };
    }
    return { draft: excludeLocation };
  }, [excludeLocation]);

  const headerTitle =
    pickerSlot?.kind === 'origin'
      ? t('m-order:wizard.location.origin_title', { defaultValue: 'Pick origin' })
      : pickerSlot
        ? t('m-order:wizard.location.destination_title', {
            defaultValue: 'Pick destination {{n}}',
            n: pickerSlot.index + 1,
          })
        : undefined;

  const canRemoveAnyDestination = destinations.length > 1;
  const effectiveClientName = customer?.name?.trim() || clientName;
  const clientId = customer?.customerId;

  return (
    <>
      <div className='space-y-2 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-xs'>
        {effectiveClientName ? (
          <div className='flex items-center justify-between gap-2'>
            <span className='font-medium text-slate-500'>
              {t('confirmation.order.client', 'Client')}
            </span>
            <span className='text-sm font-medium text-slate-800'>{effectiveClientName}</span>
          </div>
        ) : noClientHint ? (
          <div className='flex items-start gap-1.5 rounded-lg bg-blue-50 px-2 py-1.5 text-[11px] text-blue-700'>
            <InfoIcon className='mt-0.5 size-3.5 shrink-0' />
            <span>
              {t('confirmation.order.no_client_hint', {
                defaultValue:
                  'No client attached — reply with a client name to link this order to one.',
              })}
            </span>
          </div>
        ) : null}

        {driver && stagedDriverLabel(driver) ? (
          <div className='flex items-center justify-between gap-2'>
            <span className='flex items-center gap-1.5 font-medium text-slate-500'>
              <UserIcon className='size-3.5 text-slate-400' />
              {t('confirmation.order.driver', { defaultValue: 'Driver' })}
            </span>
            <span className='text-sm font-medium text-slate-800'>
              {stagedDriverLabel(driver)}
            </span>
          </div>
        ) : null}

        {items && items.length > 0 ? (
          <div className='flex items-center justify-between gap-2'>
            <span className='flex items-center gap-1.5 font-medium text-slate-500'>
              <PackageIcon className='size-3.5 text-slate-400' />
              {t('confirmation.order.items', { defaultValue: 'Load' })}
            </span>
            <span className='text-end text-sm font-medium text-slate-800'>
              {items
                .map(item => {
                  const name = item.name?.trim();
                  const weight =
                    item.weight && item.weight.value > 0
                      ? `${item.weight.value} ${t(
                          `confirmation.order.weight_units.${item.weight.unit}`,
                          { defaultValue: item.weight.unit },
                        )}`
                      : undefined;
                  return [name, weight].filter(Boolean).join(' — ');
                })
                .filter(Boolean)
                .join(', ')}
            </span>
          </div>
        ) : null}

        {notes?.trim() ? (
          <div className='flex items-start justify-between gap-2'>
            <span className='shrink-0 font-medium text-slate-500'>
              {t('confirmation.order.notes', { defaultValue: 'Notes' })}
            </span>
            <span className='text-end text-slate-700'>{notes.trim()}</span>
          </div>
        ) : null}

        <div className='space-y-2'>
          <LocationPickerRow
            tone='origin'
            label={t('confirmation.order.origin', { defaultValue: 'Origin' })}
            location={origin}
            onOpen={() => setPickerSlot({ kind: 'origin' })}
            onClear={origin?.lat ? () => setOrigin(null) : undefined}
          />

          {destinations.map((dest, idx) => {
            const hasCoords = Boolean(dest?.lat && dest?.lng && dest.lat !== 0);
            const hasNameHint = Boolean(dest?.name?.trim());
            const hasLocation = hasCoords || hasNameHint;
            const quantity = destinationVehicleQuantities[idx] ?? 1;
            return (
              <LocationPickerRow
                key={idx}
                tone='dest'
                label={t('confirmation.order.destination_n', {
                  defaultValue: 'Destination {{n}}',
                  n: idx + 1,
                })}
                location={dest}
                onOpen={() => setPickerSlot({ kind: 'destination', index: idx })}
                onClear={
                  hasLocation || canRemoveAnyDestination
                    ? () => removeDestination(idx)
                    : undefined
                }
                footer={
                  hasCoords ? (
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                      <div className='flex items-center gap-2 text-xs text-gray-500'>
                        <TruckIcon className='size-4 text-gray-500' />
                        <span>{t('confirmation.order.trucks', 'Trucks')}</span>
                      </div>
                      <div className='flex flex-wrap items-center gap-3'>
                        <DestinationPriceChip
                          index={idx}
                          onEdit={() => setPriceEditIndex(idx)}
                          source='order-draft'
                        />
                        <Counter
                          value={quantity}
                          min={1}
                          max={50}
                          onChange={n => setDestinationVehicleQuantity(idx, n)}
                        />
                      </div>
                    </div>
                  ) : undefined
                }
              />
            );
          })}

          <Button
            type='button'
            variant='outline'
            size='sm'
            className='w-full justify-center gap-1.5'
            onClick={addDestination}
          >
            <PlusIcon className='size-4' />
            {t('confirmation.order.add_destination', { defaultValue: 'Add destination' })}
          </Button>
        </div>

        <div className='flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='gap-1.5 text-xs'
            onClick={() => setDateOpen(true)}
          >
            {pickupDate
              ? formatPickupWindow(
                  { from: pickupDate.from, to: pickupDate.to },
                  getDeviceTimezone() ?? 'Asia/Riyadh',
                ) ||
                t('c-dates:pickup_window', { defaultValue: 'Pickup window' })
              : t('confirmation.order.pick_date', 'Pick date')}
          </Button>
          {destinations.length > 1 ? (
            <span className='text-slate-500'>
              {t('confirmation.order.lane_count', {
                defaultValue: '{{count}} orders',
                count: destinations.length,
              })}
            </span>
          ) : null}
        </div>
      </div>

      <LocationPickerDrawer
        isOpen={pickerSlot !== null}
        onClose={() => setPickerSlot(null)}
        title={headerTitle}
        clientId={clientId}
        excludeLocation={excludeLocation}
        initialEdit={slotInitialEdit}
        onPick={handlePick}
      />

      <PickupDateDrawer
        isOpen={isDateOpen}
        onClose={() => setDateOpen(false)}
        initialDateRange={
          pickupDate
            ? { from: new Date(pickupDate.from), to: new Date(pickupDate.to) }
            : null
        }
        onChange={({ dateRange }) =>
          setPickupDate({
            from: dateRange.from.toISOString(),
            to: dateRange.to.toISOString(),
          })
        }
      />

      {priceEditIndex !== null ? (
        <OrderPriceEditDrawer
          mode='order-draft'
          destinationIndex={priceEditIndex}
          isOpen
          onClose={() => setPriceEditIndex(null)}
        />
      ) : null}
    </>
  );
};

// Re-export so existing imports keep resolving until callers migrate.
export { agentLocationToOrderDraft as agentLocationToPublishDraft };
